/**
 * Fault Code Component for Pibot
 */
import { Component, registerComponent } from 'js/component.js';

const componentName = 'pibot-faultcode';

class PibotFaultcode extends Component {
    /**
     * Component Elements
     */
    elements = {};

    /**
     * Component State
     */
    state = {
        selectedLiteratureId: null,
        isLoading: false,
        sessionId: null,
    };

    /**
     * Open modal dialog
     */
    openModal = () => {
        if (this.elements.modal) {
            // Sync the main form values to modal form before opening
            this.syncToModal();
            this.elements.modal.showModal();
            document.body.classList.add('no-scroll');

            // Prevent auto-focus on any element by focusing on the modal itself and then blurring
            setTimeout(() => {
                this.elements.modal.focus();
                this.elements.modal.blur();
            }, 0);
        }
    };

    /**
     * Close modal dialog
     */
    closeModal = () => {
        if (this.elements.modal) {
            this.elements.modal.close();
            document.body.classList.remove('no-scroll');
        }
    };

    /**
     * Handle modal close event
     */
    handleModalClose = () => {
        document.body.classList.remove('no-scroll');
    };

    /**
     * Sync main form values to modal form
     */
    syncToModal = () => {
        // Sync dropdown selection
        if (this.elements.modalDropdown && this.elements.productFamilyDropdown) {
            this.elements.modalDropdown.value = this.elements.productFamilyDropdown.value;
        }

        // Sync search input
        if (this.elements.modalSearchInput && this.elements.faultCodeSearch) {
            this.elements.modalSearchInput.value = this.elements.faultCodeSearch.value;
            // Update hover label visibility after syncing
            this.updateSearchHoverLabel();
        }
    };

    /**
     * Sync modal form values to main form
     */
    syncFromModal = () => {
        // Sync dropdown selection
        if (this.elements.modalDropdown && this.elements.productFamilyDropdown) {
            this.elements.productFamilyDropdown.value = this.elements.modalDropdown.value;
        }

        // Sync search input
        if (this.elements.modalSearchInput && this.elements.faultCodeSearch) {
            this.elements.faultCodeSearch.value = this.elements.modalSearchInput.value;
            // Update hover label visibility after syncing
            this.updateSearchHoverLabel();
        }
    };

    /**
     * Set dropdown invalid state
     */
    setDropdownInvalid = (isInvalid, isModal = false) => {
        const dropdownField = isModal
            ? this.element.querySelector('.fault-code-lookup__modal-form .fault-code-lookup__dropdown-field')
            : this.element.querySelector('.fault-code-lookup__dropdown-field:not(.fault-code-lookup__modal-form .fault-code-lookup__dropdown-field)');

        const dropdownLabel = isModal
            ? this.element.querySelector('.fault-code-lookup__modal-form .fault-code-lookup__dropdown-label')
            : this.element.querySelector('.fault-code-lookup__dropdown-label:not(.fault-code-lookup__modal-form .fault-code-lookup__dropdown-label)');

        if (dropdownField) {
            if (isInvalid) {
                dropdownField.classList.add('fault-code-lookup__dropdown-field--invalid');
            } else {
                dropdownField.classList.remove('fault-code-lookup__dropdown-field--invalid');
            }
        }

        if (dropdownLabel) {
            if (isInvalid) {
                dropdownLabel.classList.remove('hidden');
            } else {
                dropdownLabel.classList.add('hidden');
            }
        }
    };

    /**
     * Set search field invalid state
     */
    setSearchInvalid = () => {
        // Remove this function's functionality - no longer setting search invalid states
    };

    /**
     * Update dropdown visual state based on selection
     */
    updateDropdownState = (dropdown) => {
        if (!dropdown) return;

        if (dropdown.value && dropdown.value !== '') {
            dropdown.classList.add('fault-code-lookup__dropdown-select--has-value');
        } else {
            dropdown.classList.remove('fault-code-lookup__dropdown-select--has-value');
        }
    };

    /**
     * Handle dropdown change (works for both main and modal dropdowns)
     */
    handleDropdownChange = (event) => {
        const selectedValue = event.target.value;
        const isModalDropdown = event.target.classList.contains('fault-code-lookup__modal-dropdown-select');

        // Update visual state for the dropdown that changed
        this.updateDropdownState(event.target);

        if (selectedValue) {
            // Store the selected literature_id directly
            this.state.selectedLiteratureId = selectedValue;

            // Update titles with selected product family
            this.updateTitles(selectedValue);

            // Remove invalid state
            this.setDropdownInvalid(false, isModalDropdown);

            // Sync between main and modal forms
            if (isModalDropdown) {
                if (this.elements.productFamilyDropdown) {
                    this.elements.productFamilyDropdown.value = selectedValue;
                    // Update visual state for synced dropdown
                    this.updateDropdownState(this.elements.productFamilyDropdown);
                }
                // Remove invalid state from main dropdown too
                this.setDropdownInvalid(false, false);
                // Hide modal helper text
                if (this.elements.modalHelperText) {
                    this.elements.modalHelperText.classList.add('hidden');
                }
            } else {
                if (this.elements.modalDropdown) {
                    this.elements.modalDropdown.value = selectedValue;
                    // Update visual state for synced dropdown
                    this.updateDropdownState(this.elements.modalDropdown);
                }
                // Remove invalid state from modal dropdown too
                this.setDropdownInvalid(false, true);
                // Hide main helper text
                if (this.elements.helperText) {
                    this.elements.helperText.classList.add('hidden');
                }
            }
        } else {
            // Clear selection and show helper text
            this.state.selectedLiteratureId = null;

            // Reset titles to base title
            this.updateTitles(null);

            if (isModalDropdown) {
                if (this.elements.productFamilyDropdown) {
                    this.elements.productFamilyDropdown.value = '';
                    // Update visual state for synced dropdown
                    this.updateDropdownState(this.elements.productFamilyDropdown);
                }
                // Set invalid state and show helper text for modal
                this.setDropdownInvalid(true, true);
                if (this.elements.modalHelperText) {
                    this.elements.modalHelperText.classList.remove('hidden');
                }
                // Also set invalid state for main dropdown
                this.setDropdownInvalid(true, false);
            } else {
                if (this.elements.modalDropdown) {
                    this.elements.modalDropdown.value = '';
                    // Update visual state for synced dropdown
                    this.updateDropdownState(this.elements.modalDropdown);
                }
                // Set invalid state and show helper text for main
                this.setDropdownInvalid(true, false);
                if (this.elements.helperText) {
                    this.elements.helperText.classList.remove('hidden');
                }
                // Also set invalid state for modal dropdown
                this.setDropdownInvalid(true, true);
            }
        }
    };

    /**
     * Update component titles based on selected product family
     */
    updateTitles = (selectedProductFamily) => {
        const titleElements = this.element.querySelectorAll('.fault-code-lookup__title');

        titleElements.forEach((titleElement) => {
            const baseTitle = titleElement.getAttribute('data-base-title') || 'Fault Code Lookup';

            let newTitle = baseTitle;

            if (selectedProductFamily) {
                // Get the display name for the selected product family
                const productFamilyName = this.getProductFamilyDisplayName(selectedProductFamily);
                if (productFamilyName) {
                    // Always use the format: Base Title - Product Family Name
                    newTitle = `${baseTitle} - ${productFamilyName}`;
                }
            }

            // eslint-disable-next-line no-param-reassign
            titleElement.textContent = newTitle;
        });
    };

    /**
     * Get display name for product family from dropdown options
     */
    getProductFamilyDisplayName = (value) => {
        // Try to find the display name from the dropdown options
        const dropdown = this.elements.productFamilyDropdown || this.elements.modalDropdown;
        if (dropdown) {
            const option = dropdown.querySelector(`option[value="${value}"]`);
            if (option) {
                return option.textContent.trim();
            }
        }

        // Fallback: try to find from any select element in the component
        const allSelects = this.element.querySelectorAll('select');
        // eslint-disable-next-line no-restricted-syntax
        for (const select of allSelects) {
            const option = select.querySelector(`option[value="${value}"]`);
            if (option) {
                return option.textContent.trim();
            }
        }

        return value; // Final fallback to the value itself
    };

    /**
     * Generate a unique session ID
     */
    generateSessionId = () => (
        Math.random().toString(36).substring(2, 15)
            + Math.random().toString(36).substring(2, 15)
    );

    /**
     * Base64 encode data
     */
    base64Encode = (data) => {
        // Convert to JSON string and handle Unicode characters
        const jsonString = JSON.stringify(data);

        // Use encodeURIComponent to handle Unicode, then btoa
        return btoa(
            encodeURIComponent(jsonString).replace(
                /%([0-9A-F]{2})/g,
                (match, p1) => String.fromCharCode(`0x${p1}`),
            ),
        );
    };

    /**
     * Get language, region, and locale from the page
     */
    getPageLocaleInfo = () => {
        // Helper function to get cookie value (assuming gC is a cookie getter function)
        const gC = (name) => {
            const value = `; ${document.cookie}`;
            const parts = value.split(`; ${name}=`);
            if (parts.length === 2) return parts.pop().split(';').shift();
            return null;
        };

        let locale = 'en-us';
        if (gC('langSite') !== null && gC('langSite') !== '') {
            locale = gC('langSite').replace('/', '').replace('.html', '').toLowerCase();
        } else if (document.documentElement?.lang?.includes('-')) {
            locale = document.documentElement?.lang?.toLowerCase() || 'en-us';
        }

        const isValidLanguage = (lang) => /^[a-z]{2}$/.test(lang);
        const isValidCountry = (ctry) => /^[a-z]{2,3}$/.test(ctry);

        const metaLang = document.querySelector('meta[property="language"]')?.content?.toLowerCase();
        const metaCountry = document.querySelector('meta[property="country"]')?.content?.toLowerCase();
        const [localeLang, localeCountry] = locale.toLowerCase().split('-');

        let language = 'en';
        if (isValidLanguage(metaLang)) {
            language = metaLang;
        } else if (isValidLanguage(localeLang)) {
            language = localeLang;
        }

        let country = 'us';
        if (isValidCountry(metaCountry)) {
            country = metaCountry;
        } else if (isValidCountry(localeCountry)) {
            country = localeCountry;
        }

        const regionMap = {
            na: ['ca', 'us'],
            emea: ['at', 'be', 'ch', 'cz', 'de', 'dk', 'es', 'fi', 'fr', 'gb', 'hu', 'ie', 'il', 'it', 'mde', 'nl', 'no', 'pl', 'pt', 'ro', 'ru', 'se', 'tr', 'ua', 'za'],
            latam: ['ar', 'br', 'co', 'mx', 'pr'],
            apac: ['au', 'cn', 'id', 'in', 'jp', 'kr', 'nz', 'sg', 'tw'],
        };

        let region = 'na'; // Default value

        const regionEntry = Object.entries(regionMap).find(([, countries]) => countries.includes(country));
        if (regionEntry) {
            const [regionKey] = regionEntry;
            region = regionKey;
        }

        return {
            language,
            region,
            locale: `${language}-${country}`,
        };
    };

    /**
     * Create encoded data for the API request
     */
    createEncodedData = (query) => {
        if (!this.state.sessionId) {
            this.state.sessionId = this.generateSessionId();
        }

        const { language, region, locale } = this.getPageLocaleInfo();

        const data = {
            query,
            user_session_id: this.state.sessionId,
            language,
            region,
            locale,
            current_page: window.location.href,
            source_app: 'faultcode',
        };

        return this.base64Encode(data);
    };

    /**
     * Create fault code context for the API request
     */
    createFaultCodeContext = () => {
        // Get properties from data attributes
        const topK = parseInt(this.element.getAttribute('data-top-k') || '3', 10);
        const enableHighlight = this.element.getAttribute('data-enable-highlight') === 'true';

        const context = {
            lit_id: this.state.selectedLiteratureId?.toLowerCase() || '',
            top_k: topK,
            enable_highlight: enableHighlight,
        };

        return this.base64Encode(context);
    };

    /**
     * Get HTML template from DOM
     */
    getTemplate = (templateName) => {
        const template = this.element.querySelector(
            `.fault-code-template--${templateName}`,
        );
        if (template) {
            return template.content.cloneNode(true);
        }
        return null;
    };

    /**
     * Show loading state
     */
    showLoading = () => {
        this.state.isLoading = true;
        if (this.elements.resultsContainer) {
            const loadingTemplate = this.getTemplate('loading');
            if (loadingTemplate) {
                this.elements.resultsContainer.innerHTML = '';
                this.elements.resultsContainer.appendChild(loadingTemplate);
            }
        }
    };

    /**
     * Hide loading state
     */
    hideLoading = () => {
        this.state.isLoading = false;
        const loadingSpinner = this.elements.resultsContainer?.querySelector('.loading-spinner');
        if (loadingSpinner) {
            loadingSpinner.remove();
        }
    };

    /**
     * Handle streaming response from the API
     */
    handleStreamingResponse = async (response) => {
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const reader = response.body.getReader();
        const timeout = Date.now() + 30000; // 30 second timeout

        this.hideLoading();

        // Create the main results structure using template
        const streamingTemplate = this.getTemplate('streaming');
        if (streamingTemplate) {
            this.elements.resultsContainer.innerHTML = '';
            this.elements.resultsContainer.appendChild(streamingTemplate);
        }

        const contentContainer = this.elements.resultsContainer.querySelector('.streaming-content');

        try {
            let accumulatedData = '';

            // eslint-disable-next-line no-constant-condition
            while (true) {
                const value = await reader.read(); // eslint-disable-line no-await-in-loop
                if (value.done || Date.now() > timeout) {
                    break;
                }

                let streamData = new TextDecoder().decode(value.value);
                let data = '';
                let streamFinished = false;

                // eventStream data is separated by 'data: ', if using streamingResponse, then no `data: `
                streamData = streamData.split('data: ');

                streamData.forEach((currentLine) => {
                    // remove last instance of line break from the line
                    const line = currentLine.replace(/\r\n\r\n$/, '');

                    if (line.trim() !== '[DONE]' && line.trim() !== '[END]') {
                        // Clean up the line more thoroughly
                        const cleanedLine = line
                            // Remove extra spaces within HTML tags (< div > becomes <div>)
                            .replace(/<\s*([^>]+?)\s*>/g, '<$1>')
                            // Remove extra spaces around tag names (< h 3 > becomes <h3>)
                            .replace(
                                /<\s*(\/?)\s*([a-zA-Z0-9]+)\s*([^>]*?)\s*>/g,
                                '<$1$2$3>',
                            )
                            // Clean up multiple spaces in content
                            .replace(/\s+/g, ' ')
                            // Remove spaces around common punctuation
                            .replace(/\s+([,.!?;:])/g, '$1')
                            .replace(/([,.!?;:])\s+/g, '$1 ')
                            // Clean up spaced words (like "P ump" to "Pump")
                            .replace(/\b([A-Z])\s+([a-z])/g, '$1$2');

                        data += cleanedLine;
                    }

                    if (line.trim() === '[DONE]') {
                        // Remove any <br> elements from the beginning or end of answer text
                        data = data
                            .replace(/^(<br>)+/, '')
                            .replace(/(<br>)+$/, '');
                    } else if (line.trim() === '[END]') {
                        streamFinished = true;
                    }
                });

                // Accumulate and display data
                accumulatedData += data;
                contentContainer.innerHTML = accumulatedData;

                // Scroll to bottom to show new content
                this.elements.resultsContainer.scrollTop = this.elements.resultsContainer.scrollHeight;

                if (streamFinished) {
                    break;
                }
            }
        } catch (error) {
            const streamErrorTemplate = this.getTemplate('stream-error');
            if (streamErrorTemplate) {
                const errorMessage = streamErrorTemplate.querySelector(
                    '.stream-error-message',
                );
                if (errorMessage) {
                    errorMessage.textContent = `Error reading response stream: ${error.message}`;
                }
                contentContainer.innerHTML = '';
                contentContainer.appendChild(streamErrorTemplate);
            }
        }
    };

    /**
     * Make API request to MuleSoft endpoint
     */
    makeApiRequest = async (query) => {
        try {
            const encodedData = this.createEncodedData(query);
            const faultCodeContext = this.createFaultCodeContext();

            const formData = new FormData();
            formData.append('encoded_data', encodedData);
            // formData.append("rockSigned", "");
            formData.append('faultcode_context', faultCodeContext);

            const headers = {
                client_id: '3599ada939734f6ba7560538d7dfeb49',
                client_secret: 'E61f2970FD1B4d979E03542CF665c078',
                correlation_id: 'dev-aem-ai-conversation-query',
            };

            const response = await fetch(
                'https://api-dev.rockwellautomation.com/ra-proxy-app-vpcdev/api/ai-conversation/v1/query',
                {
                    method: 'POST',
                    headers,
                    body: formData,
                },
            );

            await this.handleStreamingResponse(response);
        } catch (error) {
            this.hideLoading();
            const errorTemplate = this.getTemplate('error');
            if (errorTemplate) {
                const errorMessage = errorTemplate.querySelector('.error-message');
                if (errorMessage) {
                    errorMessage.textContent = `Failed to fetch fault code information: ${error.message}`;
                }
                this.elements.resultsContainer.innerHTML = '';
                this.elements.resultsContainer.appendChild(errorTemplate);
            }
        }
    };

    /**
     * Handle search functionality (works for both main and modal search inputs)
     */
    handleSearch = async (event) => {
        // Handle Enter key press or form submission
        if (
            event.type === 'submit'
            || (event.type === 'keypress' && event.key === 'Enter')
        ) {
            event.preventDefault(); // Prevent form submission if it's a form

            const isModalSearch = event.target.classList.contains('fault-code-lookup__modal-search-input');
            let searchValue;

            if (isModalSearch) {
                searchValue = this.elements.modalSearchInput?.value?.trim();
                // Sync to main form
                if (this.elements.faultCodeSearch) {
                    this.elements.faultCodeSearch.value = searchValue;
                }
            } else {
                searchValue = this.elements.faultCodeSearch?.value?.trim();
            }

            // Check if no product family is selected
            if (!this.state.selectedLiteratureId) {
                // Set dropdown as invalid and show helper text if no product family is selected
                this.setDropdownInvalid(true, isModalSearch);
                if (!isModalSearch) {
                    // Also set main dropdown as invalid
                    this.setDropdownInvalid(true, false);
                }

                if (isModalSearch) {
                    if (this.elements.modalHelperText) {
                        this.elements.modalHelperText.classList.remove('hidden');
                    }
                } else if (this.elements.helperText) {
                    this.elements.helperText.classList.remove('hidden');
                    // Don't open modal if no product family is selected
                }
                return; // Exit early, don't proceed with search
            }

            // Check if no search value is entered
            if (!searchValue) {
                // Show inline message in modal if no search value is entered
                if (!isModalSearch) {
                    // Only open modal if product family is selected
                    if (this.state.selectedLiteratureId) {
                        this.openModal();
                        const emptySearchTemplate = this.getTemplate('empty-search');
                        if (emptySearchTemplate) {
                            this.elements.resultsContainer.innerHTML = '';
                            this.elements.resultsContainer.appendChild(
                                emptySearchTemplate,
                            );
                        }
                    }
                } else if (this.state.selectedLiteratureId) {
                    // Already in modal, show empty search message
                    const emptySearchTemplate = this.getTemplate('empty-search');
                    if (emptySearchTemplate) {
                        this.elements.resultsContainer.innerHTML = '';
                        this.elements.resultsContainer.appendChild(
                            emptySearchTemplate,
                        );
                    }
                }
                return; // Exit early, don't proceed with search
            }

            // If we reach here, we have valid search and product family selected
            // Remove any invalid states for dropdown only
            this.setDropdownInvalid(false, false);
            this.setDropdownInvalid(false, true);

            // Open modal if searching from main form
            if (!isModalSearch) {
                this.openModal();
            }

            // Start the search
            this.showLoading();
            await this.makeApiRequest(searchValue);
        }
    };

    /**
     * Handle AI Navigator click
     */
    handleAiNavigatorClick = () => {
        try {
            const masterButton = document.querySelector('#rafb-master-button');
            if (masterButton) {
                masterButton.click();
            }

            const chatChoice = document.getElementById('rafb-chat-choice');
            if (chatChoice) {
                chatChoice.click();
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.warn('Error clicking AI Navigator elements:', error);
        }
    };

    /**
     * Handle print button click
     */
    handlePrint = () => {
        const { resultsContainer } = this.elements;
        if (!resultsContainer || !resultsContainer.innerHTML.trim()) {
            // eslint-disable-next-line no-console
            console.warn('No content to print');
            return;
        }

        // Get the streaming content specifically
        const streamingContent = resultsContainer.querySelector('.streaming-content');
        if (!streamingContent || !streamingContent.innerHTML.trim()) {
            // eslint-disable-next-line no-console
            console.warn('No streaming content to print');
            return;
        }

        // Create a new window for printing
        const printWindow = window.open('', '_blank');

        // Get the current page title for the print document
        const pageTitle = document.title || 'Fault Code Lookup Results';

        // Get the streaming content to print
        const contentToPrint = streamingContent.innerHTML;

        // Create the print document HTML
        const printHTML = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>${pageTitle}</title>
                <style>
                    body {
                        font-family: Barlow, sans-serif;
                        line-height: 1.5;
                        color: #000;
                        margin: 20px;
                        background: white;
                    }
                    .print-header {
                        display: flex;
                        align-items: center;
                        margin-bottom: 24px;
                        padding-bottom: 16px;
                        border-bottom: 1px solid #ccc;
                    }
                    .print-header img {
                        height: 40px;
                        margin-right: 16px;
                    }
                    .print-header h1 {
                        margin: 0;
                        font-size: 24px;
                        font-weight: 700;
                    }
                    h1, h2, h3, h4, h5, h6 {
                        font-weight: 700;
                        margin: 16px 0 8px 0;
                    }
                    p {
                        margin: 0 0 16px 0;
                    }
                    ul, ol {
                        margin: 0 0 16px 0;
                        padding-left: 20px;
                    }
                    li {
                        margin-bottom: 4px;
                    }
                    strong {
                        font-weight: 700;
                    }
                    @media print {
                        body {
                            margin: 0;
                        }
                        .print-header {
                            margin-bottom: 20px;
                        }
                        h1, h2, h3, h4, h5, h6 {
                            page-break-after: avoid;
                        }
                        ul, ol {
                            page-break-inside: avoid;
                        }
                    }
                </style>
            </head>
            <body>
                <div class="print-header">
                    <img src="/content/dam/rockwell-automation/sites/images/logos/2019_Logo_rgb_RA_Bug-LeftText_color.svg" alt="Rockwell Automation logo" onerror="this.style.display='none'" data-trackable="true">
                    <h1>Fault Code Lookup Results</h1>
                </div>
                ${contentToPrint}
            </body>
            </html>
        `;

        // Write the HTML to the print window
        printWindow.document.write(printHTML);
        printWindow.document.close();

        // Wait for the content to load, then print
        printWindow.onload = () => {
            printWindow.print();
            printWindow.close();
        };
    };

    /**
     * Add event listeners for dynamically created content
     */
    addDynamicEventListeners = () => {
        // Use event delegation for AI Navigator clicks since the element is created dynamically
        this.elements.resultsContainer.addEventListener('click', (event) => {
            if (event.target.classList.contains('ai-navigator-text')) {
                this.handleAiNavigatorClick();
            }
        });

        // Also handle keyboard events for accessibility
        this.elements.resultsContainer.addEventListener('keydown', (event) => {
            if (event.target.classList.contains('ai-navigator-text') && (event.key === 'Enter' || event.key === ' ')) {
                event.preventDefault();
                this.handleAiNavigatorClick();
            }
        });
    };

    /**
     * Add event listeners
     */
    addEventListeners = () => {
        // Main form event listeners
        if (this.elements.productFamilyDropdown) {
            this.elements.productFamilyDropdown.addEventListener(
                'change',
                this.handleDropdownChange,
            );
        }

        if (this.elements.faultCodeSearch) {
            this.elements.faultCodeSearch.addEventListener(
                'keypress',
                this.handleSearch,
            );

            // Clear search invalid state when user starts typing
            this.elements.faultCodeSearch.addEventListener(
                'input',
                () => {
                    // Update hover label visibility
                    this.updateSearchHoverLabel();
                }
            );

            const form = this.elements.faultCodeSearch.closest('form');
            if (form) {
                form.addEventListener('submit', this.handleSearch);
            }
        }

        // Modal form event listeners
        if (this.elements.modalDropdown) {
            this.elements.modalDropdown.addEventListener(
                'change',
                this.handleDropdownChange,
            );
        }

        if (this.elements.modalSearchInput) {
            this.elements.modalSearchInput.addEventListener(
                'keypress',
                this.handleSearch,
            );

            // Clear search invalid state when user starts typing in modal
            this.elements.modalSearchInput.addEventListener(
                'input',
                (event) => {
                    // Sync to main form
                    if (this.elements.faultCodeSearch) {
                        this.elements.faultCodeSearch.value = event.target.value;
                        // Update hover label visibility for main form too
                        this.updateSearchHoverLabel();
                    }
                    // Update hover label visibility for modal
                    this.updateSearchHoverLabel();
                }
            );

            const modalForm = this.elements.modalSearchInput.closest('form');
            if (modalForm) {
                modalForm.addEventListener('submit', this.handleSearch);
            }
        }

        // Modal event listeners
        if (this.elements.modal) {
            this.elements.modal.addEventListener('close', this.handleModalClose);

            // Close modal when clicking backdrop
            if (this.elements.modalBackdrop) {
                this.elements.modalBackdrop.addEventListener('click', () => {
                    this.closeModal();
                });
            }
        }

        // Print button event listener
        if (this.elements.printButton) {
            this.elements.printButton.addEventListener('click', this.handlePrint);
        }

        // Add dynamic event listeners for results content
        if (this.elements.resultsContainer) {
            this.addDynamicEventListeners();
        }
    };

    /**
     * Initialize component
     */
    init = () => {
        // Initialize DOM elements using class names within this component instance
        this.elements = {
            // Main form elements
            productFamilyDropdown: this.element.querySelector(
                '.fault-code-lookup__dropdown-select:not(.fault-code-lookup__modal-dropdown-select)',
            ),
            faultCodeSearch: this.element.querySelector(
                '.fault-code-lookup__search-input:not(.fault-code-lookup__modal-search-input)',
            ),
            helperText: this.element.querySelector(
                '.fault-code-lookup__helper-text:not(.fault-code-lookup__modal-helper-text)',
            ),

            // Modal elements
            modal: this.element.querySelector(
                '.fault-code-lookup__modal',
            ),
            modalBackdrop: this.element.querySelector(
                '.fault-code-lookup__modal-backdrop',
            ),
            modalDropdown: this.element.querySelector(
                '.fault-code-lookup__modal-dropdown-select',
            ),
            modalSearchInput: this.element.querySelector(
                '.fault-code-lookup__modal-search-input',
            ),
            modalHelperText: this.element.querySelector(
                '.fault-code-lookup__modal-helper-text',
            ),

            // Shared elements
            resultsContainer: this.element.querySelector(
                '.fault-code-lookup__results',
            ),

            // Print button
            printButton: this.element.querySelector(
                '.fault-code-lookup__modal-print-button',
            ),
        };

        // Check if required elements are found
        if (!this.elements.faultCodeSearch) {
            // eslint-disable-next-line no-console
            console.warn(
                'Fault code search input not found in component:',
                this.element,
            );
            return;
        }

        // Check for single product family case
        const dropdownContainer = this.element.querySelector('.fault-code-lookup__dropdown');
        const isSingleFamily = dropdownContainer && dropdownContainer.getAttribute('data-single-family') === 'true';

        if (!this.elements.productFamilyDropdown && !isSingleFamily) {
            // eslint-disable-next-line no-console
            console.warn(
                'Product family dropdown not found in component:',
                this.element,
            );
            return;
        }

        if (!this.elements.modal) {
            // eslint-disable-next-line no-console
            console.warn(
                'Modal dialog not found in component:',
                this.element,
            );
            return;
        }

        // Handle single product family case
        if (isSingleFamily && this.elements.productFamilyDropdown) {
            // Get the selected option value and set it as the literature ID
            const selectedOption = this.elements.productFamilyDropdown.querySelector('option[selected]');
            if (selectedOption) {
                this.state.selectedLiteratureId = selectedOption.value;
            }
        }

        // Initialize dropdown visual states
        if (this.elements.productFamilyDropdown) {
            this.updateDropdownState(this.elements.productFamilyDropdown);
        }
        if (this.elements.modalDropdown) {
            this.updateDropdownState(this.elements.modalDropdown);
        }

        // Add event listeners
        this.addEventListeners();

        // Initialize titles on load
        this.initializeTitles();

        // Initialize titles on load (in case there's a pre-selected value)
        if (this.elements.productFamilyDropdown && this.elements.productFamilyDropdown.value) {
            this.updateTitles(this.elements.productFamilyDropdown.value);
        }

        // Initialize hover label visibility
        this.updateSearchHoverLabel();
    };

    /**
     * Initialize titles based on current state
     */
    initializeTitles = () => {
        const titleElements = this.element.querySelectorAll('.fault-code-lookup__title');

        titleElements.forEach((titleElement) => {
            const isSingleFamily = titleElement.getAttribute('data-is-single-family') === 'true';
            const singleFamilyValue = titleElement.getAttribute('data-single-family-value');
            const baseTitle = titleElement.getAttribute('data-base-title') || 'Fault Code Lookup';

            if (isSingleFamily && singleFamilyValue) {
                // For single family, update with the family name
                const productFamilyName = this.getProductFamilyDisplayName(singleFamilyValue);

                if (productFamilyName) {
                    // Always use the format: Base Title - Product Family Name
                    const newTitle = `${baseTitle} - ${productFamilyName}`;
                    // eslint-disable-next-line no-param-reassign
                    titleElement.textContent = newTitle;
                }
            }
        });
    };

    /**
     * Update search hover label visibility based on input value
     */
    updateSearchHoverLabel = () => {
        // CSS now handles the visibility with :has(input:not(:placeholder-shown))
        // No JavaScript intervention needed
    };
}

// Register component
registerComponent(componentName, PibotFaultcode);

// Initialize components function
const initializeFaultCodeComponents = () => {
    const faultCodeElements = document.querySelectorAll('.fault-code-lookup');

    faultCodeElements.forEach((element, index) => {
        try {
            const component = new PibotFaultcode(element);
            component.init();
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(
                `Failed to initialize fault code component ${index + 1}:`,
                error,
            );
        }
    });
};

// Initialize on DOM content loaded
if (document.readyState === 'loading') {
    document.addEventListener(
        'DOMContentLoaded',
        initializeFaultCodeComponents,
    );
} else {
    // DOM is already loaded
    initializeFaultCodeComponents();
}

// Also initialize on window load as a fallback
window.addEventListener('load', () => {
    // Only initialize if no components were found before
    const existingComponents = document.querySelectorAll('.fault-code-lookup');
    if (existingComponents.length === 0) {
        initializeFaultCodeComponents();
    }
});

export default PibotFaultcode;
